import React, { useState } from 'react';
import { useLang } from '../hooks/useLang';
import { getDisplayName, getDisplayDescription } from '../utils/multilingualHelpers';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const EnhancedProgramCard = ({ program, index, showScoreBreakdown = false, isSelected = false, onSelect, className, style }) => {
  const { t, lang } = useLang();
  const [showDetails, setShowDetails] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  if (!program) return null;
  
  // Sichere Fallback-Logik
  const displayName = getDisplayName(program, lang);
  const displayDescription = getDisplayDescription(program, lang);
  
  // FIXED: Multiple fallbacks for score
  const score = program.score || program.relevance || program.matchScore || 75;
  const effort = program.applicationEffort || 'medium';

  // Use real effort percentage from database
  const effortPercentage = program.effortPercentage || getEffortPercentage(effort);
  const benefitPercentage = program.benefitPercentage || score;

  // Fallback function for effort percentage
  function getEffortPercentage(effort) {
    switch(effort) {
      case 'low': return 25;
      case 'medium': return 50;
      case 'high': return 75;
      default: return 50;
    }
  }

  // Top Badge Logic (Multilingual)
  const getTopBadge = () => {
    if (score >= 85) {
      const text = {
        de: '🔥 Empfohlen',
        es: '🔥 Recomendado', 
        it: '🔥 Raccomandato'
      };
      return { text: text[lang] || text.de, style: 'bg-emerald-100 text-emerald-800' };
    }
    if (program.deadline && new Date(program.deadline) < new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)) {
      const text = {
        de: '⏰ Kurzfristig',
        es: '⏰ Urgente',
        it: '⏰ Urgente'
      };
      return { text: text[lang] || text.de, style: 'bg-amber-100 text-amber-800 animate-pulse' };
    }
    return null;
  };

  const topBadge = getTopBadge();

  // Quick Facts (3 bullets) - Enhanced with real data
  const getQuickFacts = () => {
    const facts = [];
    
    // Fact 1: Focus with better icons
    if (program.Tags?.focus) {
      const focusIcons = {
        'direktzahlungen': '💶',
        'social': '👥',
        'biodiversity': '🦋',
        'digital': '💻',
        'innovation': '💡',
        'climate': '🌍',
        'sustainability': '🌱',
        'finance': '💰',
        'risk': '🛡️'
      };
      const focusLabels = {
        'direktzahlungen': 'Direktzahlungen',
        'social': 'Sozial',
        'biodiversity': 'Biodiversität',
        'digital': 'Digital',
        'innovation': 'Innovation',
        'climate': 'Klima',
        'sustainability': 'Nachhaltigkeit',
        'finance': 'Finanzierung',
        'risk': 'Risikoschutz'
      };
      facts.push({
        icon: focusIcons[program.Tags.focus] || '🎯',
        text: focusLabels[program.Tags.focus] || program.Tags.focus
      });
    }

    // Fact 2: Complexity & Effort (real data!)
    if (program.formPages && program.requiredDocs) {
      facts.push({
        icon: '📋',
        text: `${program.formPages} ${t('pdf.pages')}, ${program.requiredDocs} ${t('pdf.documents')}`
      });
    } else if (program.fundingRate?.percentage) {
      facts.push({
        icon: '💸',
        text: `${program.fundingRate.percentage}% ${t('pdf.fundingRate')}`
      });
    } else if (program.Fördervolumen) {
      facts.push({
        icon: '💰',
        text: program.Fördervolumen
      });
    }

    // Fact 3: User Rating (real data!)
    if (program.avgRating) {
      const stars = '⭐'.repeat(Math.round(program.avgRating));
      facts.push({
        icon: '👥',
        text: `${program.avgRating}/5 ${stars}`
      });
    } else if (program.deadline && program.deadline !== 'Laufend') {
      facts.push({
        icon: '📅',
        text: `Bis ${program.deadline.split('-').reverse().join('.')}`
      });
    } else {
      facts.push({
        icon: '📅',
        text: 'Laufend'
      });
    }

    return facts.slice(0, 3); // Max 3 facts
  };

  const quickFacts = getQuickFacts();

  // Score Badge Text (Multilingual)
  const getScoreBadge = (score) => {
    if (score >= 85) return t('card.relevance.perfect');
    if (score >= 70) return t('card.relevance.high');
    if (score >= 55) return t('card.relevance.good');
    return t('card.relevance.limited');
  };

  const handleClick = () => {
    if (onSelect) {
      onSelect(program);
    }
  };

  const generatePDF = async (e) => {
    e.stopPropagation();
    setIsGeneratingPDF(true);
    
    try {
      // Helper function to get localized date format
      const getLocalizedDate = () => {
        const date = new Date();
        switch(lang) {
          case 'es': return date.toLocaleDateString('es-ES');
          case 'it': return date.toLocaleDateString('it-IT');
          default: return date.toLocaleDateString('de-DE');
        }
      };

      // Helper function to translate funding types
      const translateFundingType = (type) => {
        if (type === 'Grant') return t('pdf.grant');
        if (type === 'Loan') return t('pdf.loan');
        if (type === 'Tax') return t('pdf.tax');
        if (type === 'Insurance Subsidy') return t('pdf.insurance');
        if (type === 'In-Kind') return t('pdf.inKind');
        return type;
      };

      // Helper function to translate target groups
      const translateTargetGroup = (target) => {
        if (target === 'Farmer') return t('pdf.farmers');
        if (target === 'Youth') return t('pdf.youth');
        if (target === 'Women') return t('pdf.women');
        if (target === 'Coop') return t('pdf.coop');
        if (target === 'Research') return t('pdf.research');
        return target;
      };

      // Helper function to translate complexity
      const translateComplexity = (complexity) => {
        if (complexity === 1) return t('pdf.simple');
        if (complexity === 2) return t('pdf.medium');
        if (complexity === 3) return t('pdf.complex');
        return complexity;
      };

      // Helper function to format deadline
      const formatDeadline = (deadline) => {
        if (deadline === '2025-12-31') return t('pdf.endOf2025');
        if (deadline && deadline !== 'Laufend') {
          return deadline.split('-').reverse().join('.');
        }
        return t('pdf.ongoing');
      };

      // Create a clean PDF content with logo and multilingual support
      const pdfContent = `
        <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px;">
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #10b981; padding-bottom: 20px;">
            <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 15px;">
              <img src="/CENTEO-C.png" alt="CENTEO Logo" style="height: 40px; margin-right: 15px;" />
              <h1 style="color: #10b981; margin: 0; font-size: 24px;">${t('pdf.title')}</h1>
            </div>
            <p style="color: #666; margin: 5px 0;">${t('pdf.generatedOn')} ${getLocalizedDate()}</p>
          </div>
          
          <div style="margin-bottom: 25px;">
            <h2 style="color: #1f2937; margin-bottom: 15px; font-size: 20px;">${displayName}</h2>
            <p style="color: #4b5563; line-height: 1.6; margin-bottom: 15px;">${displayDescription}</p>
            
            <div style="background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <h3 style="color: #374151; margin: 0 0 10px 0; font-size: 16px;">📊 ${t('pdf.evaluation')}</h3>
              <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                <span>${t('pdf.relevanceScore')}:</span>
                <strong style="color: #10b981;">${Math.round(score)}/100</strong>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                <span>${t('pdf.effort')}:</span>
                <strong style="color: ${effortPercentage < 30 ? '#10b981' : effortPercentage < 60 ? '#f59e0b' : '#ef4444'};">${effortPercentage}%</strong>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span>${t('pdf.benefit')}:</span>
                <strong style="color: #10b981;">${benefitPercentage}%</strong>
              </div>
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 25px;">
            <div>
              <h3 style="color: #374151; margin-bottom: 15px; font-size: 16px;">🏛️ ${t('pdf.basicInfo')}</h3>
              ${program.Träger ? `<p><strong>${t('pdf.carrier')}:</strong> ${program.Träger}</p>` : ''}
              ${program.Level ? `<p><strong>${t('pdf.level')}:</strong> ${program.Level}</p>` : ''}
              ${program.Förderart ? `<p><strong>${t('pdf.type')}:</strong> ${translateFundingType(program.Förderart)}</p>` : ''}
              <p><strong>${t('pdf.farmTypes')}:</strong> ${program.farmTypes?.join(', ') || t('pdf.allFarmTypes')}</p>
              ${program.Zielgruppe?.length ? `<p><strong>${t('pdf.targetGroup')}:</strong> ${program.Zielgruppe.map(translateTargetGroup).join(', ')}</p>` : ''}
            </div>
            
            <div>
              <h3 style="color: #374151; margin-bottom: 15px; font-size: 16px;">💰 ${t('pdf.financing')}</h3>
              ${program.Fördervolumen ? `<p><strong>${t('pdf.fundingVolume')}:</strong> ${program.Fördervolumen}</p>` : ''}
              ${program.fundingRate ? `<p><strong>${t('pdf.fundingRate')}:</strong> ${program.fundingRate.percentage}%</p>` : ''}
              <p><strong>${t('pdf.countries')}:</strong> ${program.countries?.join(', ') || t('pdf.euWide')}</p>
              ${program.avgRating ? `<p><strong>${t('pdf.rating')}:</strong> ${program.avgRating}/5 ⭐</p>` : ''}
            </div>
          </div>

          ${(program.formPages || program.requiredDocs) ? `
          <div style="background: #f9fafb; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
            <h3 style="color: #374151; margin: 0 0 10px 0; font-size: 16px;">📋 ${t('pdf.applicationEffort')}</h3>
            ${program.formPages ? `<p>📄 <strong>${t('pdf.form')}:</strong> ${program.formPages} ${t('pdf.pages')}</p>` : ''}
            ${program.requiredDocs ? `<p>📎 <strong>${t('pdf.documents')}:</strong> ${program.requiredDocs} ${t('pdf.pieces')}</p>` : ''}
            ${program.complexityFlag ? `<p>🧩 <strong>${t('pdf.complexity')}:</strong> ${translateComplexity(program.complexityFlag)}</p>` : ''}
          </div>
          ` : ''}

          <div style="margin-bottom: 25px;">
            <h3 style="color: #374151; margin-bottom: 15px; font-size: 16px;">⏰ ${t('pdf.timeline')}</h3>
            ${program.Zeitplan ? `<p><strong>${t('pdf.timeline')}:</strong> ${program.Zeitplan}</p>` : ''}
            <p><strong>${t('pdf.deadline')}:</strong> ${formatDeadline(program.deadline)}</p>
            ${program.combinable_with?.length ? `<p><strong>${t('pdf.combinable')}:</strong> ${program.combinable_with.length} ${t('pdf.programs')}</p>` : ''}
            ${program.Kombinierbarkeit ? `<p><strong>${t('pdf.combinability')}:</strong> ${program.Kombinierbarkeit}</p>` : ''}
          </div>

          ${program.URL ? `
          <div style="background: #eff6ff; padding: 15px; border-radius: 8px; border-left: 4px solid #3b82f6;">
            <h3 style="color: #1e40af; margin: 0 0 10px 0; font-size: 16px;">🔗 ${t('pdf.moreInfo')}</h3>
            <p style="margin: 0; word-break: break-all;">${program.URL.replace(/<[^>]*>/g, '')}</p>
          </div>
          ` : ''}

          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
            <p>${t('pdf.footer')}</p>
            <p>${t('pdf.disclaimer')}</p>
          </div>
        </div>
      `;

      // Create temporary element for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = pdfContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '800px';
      document.body.appendChild(tempDiv);

      // Generate PDF
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Clean up
      document.body.removeChild(tempDiv);

      // Download PDF
      const fileName = `CENTEO_${displayName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('PDF generation failed:', error);
      const errorMessage = lang === 'es' ? 'Error al generar PDF. Por favor, inténtelo de nuevo.' :
                          lang === 'it' ? 'Errore nella generazione del PDF. Riprova.' :
                          'PDF-Generierung fehlgeschlagen. Bitte versuchen Sie es erneut.';
      alert(errorMessage);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDetails = (e) => {
    e.stopPropagation();
    setShowDetails(!showDetails);
  };

  return (
    <div 
      className={`group relative bg-white rounded-2xl shadow-lg border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden ${
        isSelected ? 'ring-2 ring-emerald-500 border-emerald-300' : ''
      } ${showDetails ? 'col-span-full max-w-none w-full' : ''} ${className || ''}`}
      style={{
        ...style,
        ...(showDetails && { 
          gridColumn: '1 / -1',
          maxWidth: 'none',
          width: '100%'
        })
      }}
      onClick={handleClick}
    >
      {/* 1. Top Badge (optional) */}
      {topBadge && (
        <div className="absolute top-4 left-4 z-10">
          <span className={`px-3 py-1 text-xs font-bold rounded-full ${topBadge.style}`}>
            {topBadge.text}
          </span>
        </div>
      )}

      {/* Card Content */}
      <div className="p-6">
        {/* 2. Headline (max 2 lines) */}
        <div className={`${topBadge ? 'mt-8' : 'mt-0'} mb-4`}>
          <h3 className="text-xl font-bold text-slate-900 line-clamp-2 leading-tight">
            {displayName}
          </h3>
        </div>

        {/* 3. Quick Facts (3 bullets with icons) */}
        <div className="space-y-2 mb-6">
          {quickFacts.map((fact, idx) => (
            <div key={idx} className="flex items-center gap-3 text-sm">
              <span className="text-lg">{fact.icon}</span>
              <span className="text-slate-700 font-medium">{fact.text}</span>
            </div>
          ))}
        </div>

        {/* 4. Horizontal Nutzen-vs-Aufwand Bar (Real Data!) */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-slate-600">{t('card.analysis')}</span>
            <span className="text-sm font-bold text-slate-800">{effortPercentage}% {t('card.effort')}</span>
          </div>
          <div className="relative w-full bg-slate-200 rounded-full h-3 overflow-hidden">
            {/* Nutzen (grün) - using real benefitPercentage */}
            <div 
              className="absolute left-0 top-0 h-full bg-emerald-500 transition-all duration-700"
              style={{ width: `${Math.min(benefitPercentage, 100)}%` }}
            />
            {/* Aufwand (rot, von rechts) - using real effortPercentage */}
            <div 
              className="absolute right-0 top-0 h-full bg-red-500 transition-all duration-700"
              style={{ width: `${effortPercentage}%` }}
            />
            {/* Overlap area (gelb) */}
            <div 
              className="absolute top-0 h-full bg-amber-500 transition-all duration-700"
              style={{ 
                left: `${Math.max(0, benefitPercentage - effortPercentage)}%`,
                width: `${Math.min(effortPercentage, benefitPercentage)}%`
              }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>{t('card.benefit')}: {benefitPercentage}%</span>
            <span>{t('card.optimal')}: {effortPercentage < 40 && benefitPercentage > 70 ? '🎯 ' + t('card.good') : effortPercentage > 60 ? '⚠️ ' + t('card.check') : '👍 ' + t('card.good')}</span>
          </div>
          {/* Detailed Effort Breakdown */}
          {(program.formPages || program.requiredDocs || program.complexityFlag) && (
            <div className="mt-2 text-xs text-slate-500 flex gap-4">
              {program.formPages && <span>📄 {program.formPages} {t('card.pages')}</span>}
              {program.requiredDocs && <span>📎 {program.requiredDocs} {t('card.documents')}</span>}
              {program.complexityFlag && (
                <span>
                  🧩 {program.complexityFlag === 1 ? t('card.complexity.simple') : 
                       program.complexityFlag === 2 ? t('card.complexity.medium') : t('card.complexity.complex')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* 5. Relevance Score (big number) */}
        <div className="text-center mb-6 p-4 bg-slate-50 rounded-xl">
          <div className="text-4xl font-black text-slate-900 mb-1">
            {Math.round(score)} <span className="text-2xl text-slate-500">/ 100</span>
          </div>
          <div className="text-sm font-semibold text-slate-600">
            {getScoreBadge(score)}
          </div>
        </div>

        {/* 6. Action Row */}
        <div className="flex gap-3">
          <button
            onClick={handleDetails}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
          >
            {showDetails ? 'Weniger' : 'Details'}
          </button>
          <button
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            className={`px-4 py-3 rounded-xl font-semibold transition-colors ${
              isGeneratingPDF 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {isGeneratingPDF ? t('pdf.generating') : '📄'} {t('pdf.button')}
          </button>
        </div>
      </div>

      {/* Expanded Details (when showDetails is true) */}
      {showDetails && (
        <div className="border-t border-slate-200 bg-slate-50 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Grundinformationen */}
            <div>
              <h4 className="font-bold text-slate-800 mb-3">Grundinformationen</h4>
              <div className="space-y-3 text-sm">
                {program.Träger && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">🏛️ Träger:</span> 
                    <span className="font-medium">{program.Träger}</span>
                  </div>
                )}
                {program.Level && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">📊 Level:</span> 
                    <span className={`font-medium px-2 py-1 rounded text-xs ${
                      program.Level === 'National' ? 'bg-blue-100 text-blue-800' :
                      program.Level === 'Regional' ? 'bg-green-100 text-green-800' :
                      program.Level === 'Privat' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {program.Level}
                    </span>
                  </div>
                )}
                {program.Förderart && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">💳 Art:</span> 
                    <span className={`font-medium px-2 py-1 rounded text-xs ${
                      program.Förderart === 'Grant' ? 'bg-emerald-100 text-emerald-800' :
                      program.Förderart === 'Loan' ? 'bg-orange-100 text-orange-800' :
                      program.Förderart === 'Tax' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {program.Förderart === 'Grant' ? 'Zuschuss' :
                       program.Förderart === 'Loan' ? 'Darlehen' :
                       program.Förderart === 'Tax' ? 'Steuervorteil' :
                       program.Förderart === 'Insurance Subsidy' ? 'Versicherungshilfe' :
                       program.Förderart === 'In-Kind' ? 'Sachleistung' : program.Förderart}
                    </span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <span className="text-slate-500 min-w-[80px]">🚜 Betriebe:</span> 
                  <span className="font-medium">{program.farmTypes?.join(', ') || 'Alle Betriebstypen'}</span>
                </div>
                {program.Zielgruppe && program.Zielgruppe.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">👥 Zielgruppe:</span> 
                    <div className="flex flex-wrap gap-1">
                      {program.Zielgruppe.map((target, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {target === 'Farmer' ? t('pdf.farmers') :
                           target === 'Youth' ? t('pdf.youth') :
                           target === 'Women' ? t('pdf.women') :
                           target === 'Coop' ? t('pdf.coop') :
                           target === 'Research' ? t('pdf.research') : target}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Finanzierung & Aufwand */}
            <div>
              <h4 className="font-bold text-slate-800 mb-3">Finanzierung & Aufwand</h4>
              <div className="space-y-3 text-sm">
                {program.Fördervolumen && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">💰 Volumen:</span> 
                    <span className="font-medium text-green-700">{program.Fördervolumen}</span>
                  </div>
                )}
                {program.fundingRate && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">📊 Fördersatz:</span> 
                    <span className="font-medium text-green-700">{program.fundingRate.percentage}%</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <span className="text-slate-500 min-w-[80px]">🌍 Länder:</span> 
                  <span className="font-medium">{program.countries?.join(', ') || 'EU-weit'}</span>
                </div>
                {program.avgRating && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">⭐ Bewertung:</span> 
                    <span className="font-medium">{program.avgRating}/5 {'⭐'.repeat(Math.round(program.avgRating))}</span>
                  </div>
                )}
                {(program.formPages || program.requiredDocs) && (
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <div className="text-slate-600 font-medium mb-2">📋 Antragsaufwand</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {program.formPages && (
                        <div className="flex justify-between">
                          <span>📄 {t('card.form')}:</span>
                          <span className="font-medium">{program.formPages} {t('card.pages')}</span>
                        </div>
                      )}
                      {program.requiredDocs && (
                        <div className="flex justify-between">
                          <span>📎 {t('card.documents')}:</span>
                          <span className="font-medium">{program.requiredDocs} {t('card.documents_count')}</span>
                        </div>
                      )}
                      {program.complexityFlag && (
                        <div className="flex justify-between">
                          <span>🧩 Komplexität:</span>
                          <span className={`font-medium ${
                            program.complexityFlag === 1 ? 'text-green-600' :
                            program.complexityFlag === 2 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {program.complexityFlag === 1 ? t('card.complexity.simple') :
                             program.complexityFlag === 2 ? t('card.complexity.medium') : t('card.complexity.complex')}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between col-span-2 pt-1 border-t border-slate-200">
                        <span>⚡ Gesamt-Aufwand:</span>
                        <span className={`font-bold ${
                          effortPercentage < 30 ? 'text-green-600' :
                          effortPercentage < 60 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {effortPercentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Zeitplan & Kombinationen */}
            <div>
              <h4 className="font-bold text-slate-800 mb-3">Zeitplan & Kombinationen</h4>
              <div className="space-y-3 text-sm">
                {program.Zeitplan && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">⏰ Zeitplan:</span> 
                    <span className="font-medium">{program.Zeitplan}</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <span className="text-slate-500 min-w-[80px]">📅 Deadline:</span> 
                  <span className={`font-medium ${
                    program.deadline && program.deadline !== 'Laufend' && program.deadline !== '2025-12-31' 
                      ? 'text-orange-600' : 'text-green-600'
                  }`}>
                    {program.deadline === '2025-12-31' ? 'Ende 2025' :
                     program.deadline && program.deadline !== 'Laufend' ? 
                     program.deadline.split('-').reverse().join('.') : 'Laufend'}
                  </span>
                </div>
                {program.combinable_with && program.combinable_with.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 min-w-[80px]">🔗 Kombinierbar:</span> 
                    <span className="font-medium text-green-600">{program.combinable_with.length} Programme</span>
                  </div>
                )}
                {program.Kombinierbarkeit && (
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <div className="text-yellow-800 font-medium mb-1">💡 Kombinierbarkeit</div>
                    <div className="text-yellow-700 text-xs">{program.Kombinierbarkeit}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vollständige Beschreibung */}
          {displayDescription && (
            <div className="mt-6 p-4 bg-white rounded-lg">
              <h4 className="font-bold text-slate-800 mb-2">Beschreibung</h4>
              <p className="text-sm text-slate-700 leading-relaxed">{displayDescription}</p>
            </div>
          )}

          {/* URL Link */}
          {program.URL && (
            <div className="mt-4">
              <a 
                href={program.URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-800 font-semibold text-sm"
                onClick={(e) => e.stopPropagation()}
              >
                🔗 Weitere Informationen
              </a>
            </div>
          )}
        </div>
      )}

      {/* Hover Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/5 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl" />
    </div>
  );
};

export default EnhancedProgramCard;